/**
 * Product API Service
 * 
 * Fetches products from Open Food Facts (OFF) and Open Beauty Facts (OBF),
 * with caching, rate limiting, and normalization.
 * 
 * Flow: cache → OFF → OBF → not found
 * 
 * Licensing: OFF/OBF data is under ODbL. Attribution is required.
 */

import { consumeToken } from './rateLimit';
import { getCached, setCache } from './productCache';

const REQ_CONTACT = process.env.REACT_APP_USER_AGENT_CONTACT || 'toxaware-app@users.local'; // eslint-disable-line no-unused-vars

// API requests are proxied to avoid CORS issues:
// - Dev: CRA proxy (setupProxy.js)
// - Production: Vercel rewrites (vercel.json)
const OFF_BASE = '/off-api';
const OFF_SEARCH_BASE = '/off-search';
const OBF_BASE = '/obf-api';

const PRODUCT_FIELDS = [
    'code', 'product_name', 'brands', 'categories_tags',
    'ingredients_text', 'ingredients', 'nutriments',
    'allergens_tags', 'additives_tags', 'nova_group',
    'nutrition_grades', 'images', 'image_front_url'
];


async function apiFetch(url, retries = 3) {
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const response = await fetch(url);

            if ((response.status === 503 || response.status === 500) && attempt < retries) {
                // Server error — wait longer each time
                await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
                continue;
            }

            if (!response.ok) {
                throw new Error(`API error: ${response.status} ${response.statusText}`);
            }

            return response.json();
        } catch (e) {
            if (attempt < retries && e.message.includes('Failed to fetch')) {
                await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
                continue;
            }
            throw e;
        }
    }
}

/**
 * Normalize an OFF/OBF product response into our app's shape.
 */
function normalizeProduct(raw, source) {
    const p = raw.product || raw || {};
    if (!p.code) console.log('[ProductAPI] Normalizing product with no code:', p);

    // Infer leave-on vs rinse-off from categories
    const rawCats = p.categories_tags || [];
    const categories = Array.isArray(rawCats) ? rawCats.map(c => String(c).toLowerCase()) : [];
    const isLeaveOn = inferLeaveOn(categories, source);

    // Get best image URL
    const imageUrl = p.image_front_url || p.image_url ||
        (p.images?.front?.display?.url) || null;

    return {
        barcode: p.code || '',
        name: p.product_name || 'Unknown Product',
        brand: p.brands || '',
        category: inferCategory(categories, source),
        subcategory: categories[0]?.replace(/^en:/, '') || '',
        ingredients: p.ingredients_text || '',
        ingredients_tags: p.ingredients?.map(i => i.id) || [],
        nova_group: p.nova_group || null,
        additives_tags: p.additives_tags || [],
        nutriments: p.nutriments || {},
        allergens_tags: p.allergens_tags || [],
        nutrition_grades: p.nutrition_grades || null,
        image_url: imageUrl,
        isLeaveOn,
        source,
        id: `${source}-${p.code}`,
    };
}

function inferCategory(categories, source) {
    if (source === 'obf') return 'personal-care';

    const catStr = categories.join(' ');

    // Detect personal care products even when indexed in OFF
    const personalCareKeywords = [
        'cream', 'lotion', 'shampoo', 'soap', 'deodorant', 'sunscreen',
        'cosmetic', 'beauty', 'body-care', 'skin-care', 'hair-care',
        'hygiene', 'perfume', 'moisturiz', 'conditioner', 'toothpaste',
        'shower', 'body-wash', 'face-wash', 'lip-balm', 'mascara',
        'make-up', 'makeup', 'nail', 'aftershave', 'antiperspirant',
        'personal-care', 'skin', 'non-food', 'non-alimentaire',
    ];
    if (personalCareKeywords.some(k => catStr.includes(k))) return 'personal-care';

    if (catStr.includes('baby') || catStr.includes('infant')) return 'baby-care';
    if (catStr.includes('clean') || catStr.includes('detergent') || catStr.includes('household'))
        return 'household';
    return 'food';
}

function inferLeaveOn(categories, source) {
    if (source !== 'obf') return false;
    const catStr = categories.join(' ');
    const rinseOff = ['shampoo', 'soap', 'wash', 'cleanser', 'scrub', 'rinse'];
    return !rinseOff.some(k => catStr.includes(k));
}

// ========== PUBLIC API ==========

/**
 * Fetch a product by barcode.
 * Checks cache first, then OFF, then OBF.
 * 
 * @param {string} barcode
 * @returns {Promise<{product: object|null, source: string, fromCache: boolean, error: string|null}>}
 */
export async function fetchByBarcode(barcode) {
    const cleanBarcode = barcode.trim().replace(/[^0-9]/g, '');
    if (!cleanBarcode || cleanBarcode.length < 4) {
        return { product: null, source: null, fromCache: false, error: 'Invalid barcode' };
    }

    // 1) Check cache (OFF then OBF)
    for (const src of ['off', 'obf']) {
        const cached = getCached(src, cleanBarcode);
        if (cached && cached.name) {
            return { product: cached, source: src, fromCache: true, error: null };
        }
    }

    // 2) Rate limit check
    const rateCheck = consumeToken('lookup');
    if (!rateCheck.allowed) {
        return {
            product: null,
            source: null,
            fromCache: false,
            error: `Rate limited. Try again in ${Math.ceil(rateCheck.retryAfterMs / 1000)}s.`,
        };
    }

    // 3) Try OFF
    try {
        const fields = PRODUCT_FIELDS.join(',');
        const url = `${OFF_BASE}/api/v2/product/${cleanBarcode}.json?fields=${fields}`;
        const data = await apiFetch(url);

        if (data.status === 1 && data.product) {
            const normalized = normalizeProduct(data, 'off');
            setCache('off', cleanBarcode, normalized, data);
            return { product: normalized, source: 'off', fromCache: false, error: null };
        }
    } catch (e) {
        console.warn('OFF lookup failed:', e.message);
    }

    // 4) Try OBF
    const rateCheck2 = consumeToken('lookup');
    if (!rateCheck2.allowed) {
        return {
            product: null,
            source: null,
            fromCache: false,
            error: `Rate limited. Try again in ${Math.ceil(rateCheck2.retryAfterMs / 1000)}s.`,
        };
    }

    try {
        const fields = PRODUCT_FIELDS.join(',');
        const url = `${OBF_BASE}/api/v2/product/${cleanBarcode}.json?fields=${fields}`;
        const data = await apiFetch(url);

        if (data.status === 1 && data.product) {
            const normalized = normalizeProduct(data, 'obf');
            setCache('obf', cleanBarcode, normalized, data);
            return { product: normalized, source: 'obf', fromCache: false, error: null };
        }
    } catch (e) {
        console.warn('OBF lookup failed:', e.message);
    }

    // 5) Not found anywhere
    return { product: null, source: null, fromCache: false, error: null };
}

/**
 * Search products by text query via OFF.
 * Respects 10 req/min limit. Do NOT call this on every keystroke.
 * 
 * @param {string} query
 * @param {number} page
 * @param {number} pageSize
 * @returns {Promise<{products: object[], error: string|null, total: number}>}
 */
export async function searchProducts(query, page = 1, pageSize = 10) {
    const trimmed = query.trim();
    if (trimmed.length < 2) return { products: [], error: null };

    const rateCheck = consumeToken('search');
    if (!rateCheck.allowed) {
        return { products: [], error: `Search rate limited. Try again in ${Math.ceil(rateCheck.retryAfterMs / 1000)}s.` };
    }

    try {
        const encoded = encodeURIComponent(trimmed);
        // Use modern Elasticsearch search API (proxied, bypasses legacy search.pl entirely)
        const url = `${OFF_SEARCH_BASE}/search?q=${encoded}&page_size=${pageSize}&page=${page}`;
        const data = await apiFetch(url);

        // Elasticsearch API returns 'hits' instead of 'products'
        if (data.hits && data.hits.length > 0) {
            const normalized = data.hits
                .filter(p => p.product_name)
                .map(p => normalizeProduct({ product: p }, 'off'));

            for (const prod of normalized) {
                if (prod.barcode) setCache('off', prod.barcode, prod);
            }

            return { products: normalized, total: data.count || normalized.length, error: null };
        }

        return { products: [], error: null };
    } catch (e) {
        console.warn('OFF search failed:', e.message);
        return { products: [], error: `Search failed: ${e.message}` };
    }
}

/**
 * Search OBF for cosmetics by text query.
 * 
 * @param {string} query
 * @param {number} page
 * @param {number} pageSize
 */
export async function searchBeautyProducts(query, page = 1, pageSize = 10) {
    const trimmed = query.trim();
    if (trimmed.length < 2) return { products: [], error: null };

    const rateCheck = consumeToken('search');
    if (!rateCheck.allowed) {
        return { products: [], error: `Search rate limited. Try again in ${Math.ceil(rateCheck.retryAfterMs / 1000)}s.` };
    }

    try {
        const encoded = encodeURIComponent(trimmed);
        // No &fields= param — keeps the query lightweight to avoid 503s
        const url = `${OBF_BASE}/cgi/search.pl?search_terms=${encoded}&search_simple=1&action=process&json=1&page_size=${pageSize}&page=${page}`;
        const data = await apiFetch(url, 1); // Only 1 retry — OBF server is unreliable

        if (data.products && data.products.length > 0) {
            const normalized = data.products
                .filter(p => p.product_name)
                .map(p => normalizeProduct({ product: p }, 'obf'));

            for (const prod of normalized) {
                if (prod.barcode) setCache('obf', prod.barcode, prod);
            }

            return { products: normalized, total: data.count || normalized.length, error: null };
        }

        return { products: [], error: null };
    } catch (e) {
        console.warn('OBF search failed:', e.message);
        return { products: [], error: `Search failed: ${e.message}` };
    }
}
